import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    buildCriticalMediaLoadIds,
    buildMediaLoadOrder,
} from './loadPlanner';
import { useMediaThumbnails } from './useMediaThumbnails';

const MAX_MEDIA_THUMBNAIL_CONCURRENCY = 2;

function scheduleIdleWork(callback) {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        return {
            cancel: window.cancelIdleCallback.bind(window),
            id: window.requestIdleCallback(callback, { timeout: 250 }),
        };
    }

    return {
        cancel: window.clearTimeout.bind(window),
        id: window.setTimeout(() => callback({
            didTimeout: false,
            timeRemaining: () => 0,
        }), 150),
    };
}

export function useScanMediaCarouselController(mediaScans) {
    const [selectedIndexState, setSelectedIndex] = useState(0);
    const [visibleThumbnailIds, setVisibleThumbnailIds] = useState([]);
    const [idleEligibleIds, setIdleEligibleIds] = useState([]);
    const idleWorkHandleRef = useRef(null);
    const selectedIndex = mediaScans.length === 0
        ? 0
        : Math.min(selectedIndexState, mediaScans.length - 1);

    useEffect(() => {
        setSelectedIndex((currentIndex) => {
            if (mediaScans.length === 0) {
                return 0;
            }

            return Math.min(currentIndex, mediaScans.length - 1);
        });
    }, [mediaScans.length]);

    useEffect(() => {
        const activeScanIds = new Set(mediaScans.map((scan) => scan.id));

        setVisibleThumbnailIds((currentVisibleIds) => currentVisibleIds
            .filter((scanId) => activeScanIds.has(scanId)));
        setIdleEligibleIds((currentIdleEligibleIds) => currentIdleEligibleIds
            .filter((scanId) => activeScanIds.has(scanId)));
    }, [mediaScans]);

    const criticalLoadIds = useMemo(
        () => buildCriticalMediaLoadIds(mediaScans, selectedIndex, visibleThumbnailIds),
        [mediaScans, selectedIndex, visibleThumbnailIds],
    );
    const idleCandidateIds = useMemo(() => {
        const criticalLoadIdSet = new Set(criticalLoadIds);

        return mediaScans
            .map((scan) => scan.id)
            .filter((scanId) => !criticalLoadIdSet.has(scanId));
    }, [criticalLoadIds, mediaScans]);
    const desiredLoadOrder = useMemo(
        () => buildMediaLoadOrder(mediaScans, selectedIndex, visibleThumbnailIds, idleEligibleIds),
        [idleEligibleIds, mediaScans, selectedIndex, visibleThumbnailIds],
    );
    const {
        haveAllMediaFailed,
        thumbnailStates,
    } = useMediaThumbnails({
        desiredLoadOrder,
        maxConcurrentLoads: MAX_MEDIA_THUMBNAIL_CONCURRENCY,
        mediaScans,
    });

    useEffect(() => {
        const idleWorkHandle = idleWorkHandleRef.current;

        if (idleWorkHandle) {
            idleWorkHandle.cancel(idleWorkHandle.id);
            idleWorkHandleRef.current = null;
        }

        const nextIdleCandidateId = idleCandidateIds.find(
            (scanId) => !idleEligibleIds.includes(scanId),
        );

        if (!nextIdleCandidateId) {
            return undefined;
        }

        const scheduledIdleWork = scheduleIdleWork(() => {
            setIdleEligibleIds((currentIdleEligibleIds) => (
                currentIdleEligibleIds.includes(nextIdleCandidateId)
                    ? currentIdleEligibleIds
                    : [
                        ...currentIdleEligibleIds,
                        nextIdleCandidateId,
                    ]
            ));
        });

        idleWorkHandleRef.current = scheduledIdleWork;

        return () => {
            scheduledIdleWork.cancel(scheduledIdleWork.id);
        };
    }, [idleCandidateIds, idleEligibleIds]);

    return {
        haveAllMediaFailed,
        selectedIndex,
        setSelectedIndex,
        setVisibleThumbnailIds,
        thumbnailStates,
        visibleThumbnailIds,
    };
}
